/**
 * InvoiceSummaryCard — Card flutuante "Sua fatura chegou!"
 *
 * v2.0: card mais compacto (paddings/typography reduzidos para se aproximar
 * do modelo de referência), referência exibe N. da fatura quando disponível
 * e a seção de itens lista descrição, quantidade, valor unitário e total
 * (com soma final).
 *
 * RELEASE: 2026-06-27 v2.7.0 — Logo movida para o canto superior esquerdo
 *          do card em formato quadrado; logo sobreposta central removida
 *          do InvoiceHeader. Aproveita o espaço liberado no centro do card.
 * RELEASE: 2026-06-21 v2.4.0 — `frequencia` agora pode chegar como
 *          "Recorrente - Mensal" (ou outra periodicidade). Nenhum
 *          ajuste estrutural: o layout existente já suporta o texto
 *          maior via flex wrap.
 * RELEASE: 2026-06-21 v2.3.0 — Exibe " - Parcela X de Y" ao lado do
 *          número da fatura quando o link é de parcela específica.
 * RELEASE: 2026-06-21 v2.1.0 — Substitui badge pill por bolinha colorida
 *          semântica + texto preto (verde só quando paga, âmbar pendente,
 *          vermelho estornada, cinza cancelada/indisponível).
 * RELEASE: 2026-06-21 v2.0.0 — Compacta o card, troca referência por
 *          numero_fatura e adiciona tabela de itens detalhada.
 * RELEASE: 2026-06-21 v1.0.0 — Versão inicial.
 */

import { sanitizePlain } from '@/lib/checkout/sanitizePlain';

interface InvoiceItem {
  descricao: string;
  quantidade: number;
  valor_unitario: number;
  total: number;
}

interface InvoiceSummaryCardProps {
  status: string;
  terminal: boolean;
  /** Número da fatura amigável (preferencial). */
  numeroFatura?: string | null;
  valorFormatado: string;
  vencimentoFormatado: string;
  frequencia: string;
  descricao?: string | null;
  itens?: InvoiceItem[];
  /** Total bruto da cobrança (já formatado). Exibido no rodapé da tabela. */
  totalFormatado?: string;
  /** v2.3.0 — Quando o link é de uma parcela específica, exibe " - Parcela X de Y" após o número da fatura. */
  numeroParcela?: number | null;
  totalParcelas?: number | null;
  /** v2.7.0 — Logo do emissor exibida no canto superior esquerdo do card (quadrada). */
  logoUrl?: string | null;
  sellerName?: string | null;
}


// Mapa de status -> { rótulo, cor da bolinha semântica }
// Verde apenas para "pago"; âmbar = pendente; vermelho = estornado; cinza = cancelado/indisponível.
const STATUS_LABEL: Record<string, { text: string; dotCls: string }> = {
  pago: { text: 'Fatura paga', dotCls: 'bg-emerald-500' },
  cancelado: { text: 'Cobrança cancelada', dotCls: 'bg-slate-400' },
  estornado: { text: 'Cobrança estornada', dotCls: 'bg-rose-500' },
  ativo: { text: 'Fatura pendente', dotCls: 'bg-amber-500' },
  emitido: { text: 'Fatura pendente', dotCls: 'bg-amber-500' },
  // v2.2.0 — quando o link é de uma parcela específica, o badge reflete o
  // status da parcela (não da fatura inteira). Mantém o mesmo esquema de cor.
  pago_parcela: { text: 'Parcela paga', dotCls: 'bg-emerald-500' },
  pendente_parcela: { text: 'Parcela pendente', dotCls: 'bg-amber-500' },
  cancelado_parcela: { text: 'Parcela cancelada', dotCls: 'bg-slate-400' },
  estornado_parcela: { text: 'Parcela estornada', dotCls: 'bg-rose-500' },
};

function statusBadge(status: string, terminal: boolean) {
  if (STATUS_LABEL[status]) return STATUS_LABEL[status];
  return terminal
    ? { text: 'Indisponível', dotCls: 'bg-slate-400' }
    : { text: 'Fatura pendente', dotCls: 'bg-amber-500' };
}


// Formatador local (não recebemos `moeda` aqui — itens vêm sempre em BRL no fluxo atual)
function fmtBRL(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

export function InvoiceSummaryCard({
  status,
  terminal,
  numeroFatura,
  valorFormatado,
  vencimentoFormatado,
  frequencia,
  descricao,
  itens,
  totalFormatado,
  numeroParcela,
  totalParcelas,
  logoUrl,
  sellerName,
}: InvoiceSummaryCardProps) {
  const badge = statusBadge(status, terminal);
  const hasItens = Array.isArray(itens) && itens.length > 0;
  const totalLinha = totalFormatado ?? valorFormatado;

  return (
    // No mobile a sobreposição é menor para não cortar o cabeçalho
    <div className="px-3 md:px-9 -mt-2 md:-mt-8 relative z-10">
      {/* Trilho interno: ~15% mais estreito que a área útil externa (md:max-w-[56.5rem]). */}
      <div className="md:max-w-[56.5rem] md:mx-auto bg-white rounded-2xl md:rounded-3xl p-4 md:p-6 border border-slate-100 shadow-xl shadow-slate-900/5">
        {/* Cabeçalho com logo quadrada no canto superior esquerdo e conteúdo à direita.
            RELEASE: 2026-06-27 v2.7.0 — Layout flex com logo quadrada (64×64) à esquerda. */}
        <div className="flex items-start gap-3 md:gap-4 mb-3 md:mb-5">
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={sellerName ? `Logo ${sellerName}` : 'Logo'}
              className="w-16 h-16 rounded-xl object-contain border border-slate-100 bg-white shadow-sm shrink-0"
            />
          ) : (
            <div
              className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-100 shrink-0"
              aria-hidden
            />
          )}

          <div className="flex-1 min-w-0">
            {/* Status + número da fatura */}
            <div className="flex flex-wrap justify-between items-start gap-2 mb-3 md:mb-4">
              <span className="inline-flex items-center gap-2 text-sm font-semibold text-slate-900">
                <span className={`w-2 h-2 rounded-full ${badge.dotCls}`} aria-hidden />
                {badge.text}
              </span>
              {numeroFatura && (
                <p className="text-slate-400 text-sm font-medium">
                  Fatura: <span className="text-slate-600 font-semibold">#{numeroFatura}</span>
                  {numeroParcela && totalParcelas && totalParcelas > 1 && (
                    <>
                      {' '}
                      <span className="text-slate-400">-</span>{' '}
                      <span className="text-slate-600 font-semibold">
                        Parcela {numeroParcela} de {totalParcelas}
                      </span>
                    </>
                  )}
                </p>
              )}
            </div>

            {/* Título de boas-vindas — varia conforme estado da fatura.
                RELEASE: 2026-06-27 v2.9.0 — "Sua fatura chegou!" para pendentes
                e títulos contextuais para estados terminais. */}
            <h2 className="text-xl sm:text-2xl md:text-[28px] font-bold text-slate-900 tracking-tight mb-3 md:mb-4">
              {status === 'pago' || status === 'pago_parcela'
                ? 'Pagamento confirmado!'
                : terminal
                  ? badge.text
                  : 'Sua fatura chegou!'}
            </h2>

            {/* Valor × Vencimento — divisor vertical central e vencimento alinhado
                logo após a barra (à esquerda do bloco direito).
                RELEASE: 2026-06-27 v2.9.0 — Bloco direito com `flex-none` e
                `items-start text-left` para que a data fique colada à barra. */}
            <div className="flex items-stretch">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-slate-500 mb-1 sm:mb-1.5">
                  Valor a pagar
                </p>
                <p className="text-xl sm:text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
                  {valorFormatado}
                </p>
              </div>
              <div className="w-px bg-slate-200 mx-4 md:mx-10" aria-hidden />
              <div className="flex-none min-w-0 flex flex-col items-start text-left">
                <p className="text-xs sm:text-sm font-medium text-slate-500 mb-1 sm:mb-1.5">
                  Vencimento
                </p>
                <p className="text-xl sm:text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">
                  {vencimentoFormatado}
                </p>
              </div>
            </div>
          </div>
        </div>


        {/* Frequência */}
        <div className="py-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2">
          <p className="text-sm font-medium text-slate-500">
            Frequência
          </p>
          <p className="text-base font-bold text-slate-900">{frequencia}</p>
        </div>

        {/* Itens da cobrança — tabela detalhada (preferencial) */}
        {hasItens ? (
          <div className="pt-4 border-t border-slate-100">
            <p className="text-sm font-medium text-slate-500 mb-3">
              Itens da cobrança
            </p>

            {/* Desktop: tabela */}
            <div className="hidden sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs font-medium text-slate-500">
                    <th className="text-left pb-2 font-medium">Item</th>
                    <th className="text-center pb-2 font-medium w-16">Qtd</th>
                    <th className="text-right pb-2 font-medium w-28">Unit.</th>
                    <th className="text-right pb-2 font-medium w-32">Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {itens!.map((it, idx) => (
                    <tr key={idx} className="text-slate-700">
                      <td className="py-2 pr-2 font-medium">{sanitizePlain(it.descricao)}</td>
                      <td className="py-2 text-center tabular-nums">{it.quantidade}</td>
                      <td className="py-2 text-right tabular-nums">{fmtBRL(it.valor_unitario)}</td>
                      <td className="py-2 text-right tabular-nums font-semibold">
                        {fmtBRL(it.total)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t border-slate-200">
                    <td colSpan={3} className="pt-3 text-right text-sm font-semibold text-slate-500">
                      Total
                    </td>
                    <td className="pt-3 text-right text-base font-bold text-slate-900 tabular-nums">
                      {totalLinha}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Mobile: cards empilhados */}
            <div className="sm:hidden space-y-2">
              {itens!.map((it, idx) => (
                <div
                  key={idx}
                  className="rounded-xl border border-slate-100 p-2.5 space-y-1"
                >
                  <p className="text-sm font-semibold text-slate-800">
                    {sanitizePlain(it.descricao)}
                  </p>
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span className="tabular-nums">
                      {it.quantidade} × {fmtBRL(it.valor_unitario)}
                    </span>
                    <span className="font-semibold text-slate-900 tabular-nums">
                      {fmtBRL(it.total)}
                    </span>
                  </div>
                </div>
              ))}
              <div className="flex items-center justify-between pt-2 border-t border-slate-200">
                <span className="text-sm font-semibold text-slate-500">
                  Total
                </span>
                <span className="text-base font-bold text-slate-900 tabular-nums">
                  {totalLinha}
                </span>
              </div>
            </div>
          </div>
        ) : (
          // Fallback: cobranças sem itens vinculados — mantém descrição livre
          descricao && (
            <div className="space-y-1 pt-4 border-t border-slate-100">
              <p className="text-sm font-medium text-slate-500">
                Descrição
              </p>
              <p className="text-slate-700 font-medium whitespace-pre-line">
                {sanitizePlain(descricao)}
              </p>
            </div>
          )
        )}

        {/* Descrição da cobrança — exibida também quando há itens, pois pode
            conter informações renderizadas por variáveis (ex.: placas). */}
        {hasItens && descricao && (
          <div className="space-y-1 pt-4 border-t border-slate-100">
            <p className="text-sm font-medium text-slate-500">Descrição</p>
            <p className="text-slate-700 font-medium whitespace-pre-line">
              {sanitizePlain(descricao)}
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
